import {
  useLocationServices,
  useLocationVisitHistory,
} from '../hooks/useLocationOperations';
import { useI18n } from '../i18n/I18nProvider';
import { formatDateTime } from '../lib/formatters';
import { SkeletonRow } from './Skeleton';
import { StatusBadge } from './StatusBadge';

export function LocationServices({ locationId }: { locationId: string }) {
  const { t } = useI18n();
  const query = useLocationServices(locationId);
  return (
    <section className="space-y-4" aria-labelledby="location-services-heading">
      <div>
        <h2 id="location-services-heading" className="font-semibold text-text">
          {t('location.services.title')}
        </h2>
        <p className="mt-1 text-sm text-text-muted">
          {t('location.services.hint')}
        </p>
      </div>
      {query.isLoading ? (
        <SkeletonRow />
      ) : query.isError ? (
        <p role="alert" className="text-sm text-notification">
          {t('location.services.error')}{' '}
          <button
            type="button"
            className="min-h-11 underline"
            onClick={() => void query.refetch()}
          >
            {t('common.retry')}
          </button>
        </p>
      ) : !query.data?.length ? (
        <p className="rounded-lg border border-border bg-surface p-5 text-sm text-text-muted">
          {t('location.services.empty')}
        </p>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {query.data.map((service) => (
            <article
              key={service.id}
              className="rounded-lg border border-border bg-surface p-4 sm:p-5"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="break-words text-sm font-semibold text-text">
                    {service.code ??
                      service.provider ??
                      t('location.services.service')}
                  </h3>
                  <p className="mt-1 text-xs text-text-muted">
                    {[
                      service.provider,
                      service.type.replace(/_/g, ' ').toUpperCase(),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
                <StatusBadge status={service.status} />
              </div>
              <dl className="mt-4 grid grid-cols-2 gap-4 text-sm">
                <ServiceFact
                  title={t('location.services.circuit')}
                  value={service.circuit_id}
                />
                <ServiceFact
                  title={t('location.services.bandwidth')}
                  value={
                    service.bandwidth_label ??
                    [
                      service.bandwidth_down_mbps == null
                        ? null
                        : `↓ ${service.bandwidth_down_mbps}`,
                      service.bandwidth_up_mbps == null
                        ? null
                        : `↑ ${service.bandwidth_up_mbps}`,
                    ]
                      .filter(Boolean)
                      .join(' / ') +
                      (service.bandwidth_down_mbps != null ||
                      service.bandwidth_up_mbps != null
                        ? ' Mbps'
                        : '')
                  }
                />
                <ServiceFact
                  title={t('location.services.transport')}
                  value={[service.transport, service.handoff]
                    .filter(Boolean)
                    .join(' / ')}
                />
                <ServiceFact
                  title={t('location.services.sla')}
                  value={[
                    service.sla_target,
                    service.mttr_hours == null
                      ? null
                      : t('location.services.restoration', {
                          n: service.mttr_hours,
                        }),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                />
              </dl>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

export function LocationVisitHistory({ locationId }: { locationId: string }) {
  const { t } = useI18n();
  const query = useLocationVisitHistory(locationId);
  return (
    <section
      className="mb-4 border-b border-border pb-4"
      aria-labelledby="location-visit-history-heading"
    >
      <h3
        id="location-visit-history-heading"
        className="mb-2 text-sm font-medium text-text-muted"
      >
        {t('location.visits.title')}
      </h3>
      {query.isLoading ? (
        <SkeletonRow />
      ) : query.isError ? (
        <p role="alert" className="text-sm text-notification">
          {t('location.visits.error')}
        </p>
      ) : !query.data?.length ? (
        <p className="text-sm text-text-faint">{t('location.visits.empty')}</p>
      ) : (
        <ul className="divide-y divide-divider">
          {query.data.map((visit) => (
            <li key={visit.id} className="py-3">
              <p className="text-sm font-medium text-text">
                {formatDateTime(visit.visited_at)}
                {visit.technician_name ? ` · ${visit.technician_name}` : ''}
              </p>
              {visit.work_performed && (
                <p className="mt-1 whitespace-pre-wrap text-sm text-text-muted">
                  {visit.work_performed}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ServiceFact({
  title,
  value,
}: {
  title: string;
  value: string | null;
}) {
  return (
    <div>
      <dt className="text-xs text-text-faint">{title}</dt>
      <dd className="mt-1 break-words text-text">{value || '—'}</dd>
    </div>
  );
}
